import { useCallback, useContext, useState } from "react";
import { AxiosError } from "axios";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import { HttpClientContext, X_REQUEST_ID } from "../../../app/client";
import { Task } from "../../../app/backendTypes";
import { useAppSelector } from "../../../app/hooks";
import { Role } from "../../../app/backendTypes";
import { Close } from "@mui/icons-material";
import Morselbar from "../Morselbar";

export default function RetryTask(props: { task: Task; callback: () => void }) {
  const { task, callback } = props;

  const statusKind = task.status.kind;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const client = useContext(HttpClientContext);

  const retryTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    client
      ?.post(`/api/tasks/${task.id}/retry`)
      .then(() => {
        callback();
        setDialogOpen(false);
      })
      .catch((e: AxiosError) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client, task, callback]);

  const auth = useAppSelector((state) => state.auth);

  if (statusKind !== "Error") return <></>;

  if (!auth.roles.includes(Role.Administrator)) return <></>;

  return (
    <>
      <Button
        color="warning"
        disabled={loading}
        onClick={() => setDialogOpen(true)}
        variant="outlined"
      >
        Retry
      </Button>

      <Dialog open={dialogOpen}>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography>
              This action will{" "}
              <Typography
                component="span"
                color="warning.main"
                fontWeight="bold"
              >
                restart
              </Typography>{" "}
              the task
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          <br />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => retryTask()}
              disabled={loading}
              color="warning"
              variant="outlined"
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to run task"
        error={error}
      />
    </>
  );
}
